import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { ConsultationConnectorAccessType, ConsultationProvider, FamilyLinkType, IPlatform, Module, ProfileField } from '@bciti/types';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { Subject } from 'rxjs';

@Component({
    selector: 'app-platform-form',
    templateUrl: './platform-form.component.html',
    styleUrls: ['./platform-form.component.scss'],
})
export class PlatformFormComponent implements OnInit {
    destroy$ = new Subject();

    @Input()
    platform: FormGroup;

    @Input()
    loading: boolean = false;

    @Input()
    displayActions: boolean = true;

    @Output()
    submitForm = new EventEmitter<Partial<IPlatform>>();

    modules: Module[] = Object.values(Module);
    familyLinks: FamilyLinkType[] = Object.values(FamilyLinkType);
    consultationConnectorAccessType = Object.values(ConsultationConnectorAccessType);
    consultationProvider = Object.values(ConsultationProvider);
    hiddenFieldsList: ProfileField[] = Object.values(ProfileField);
    platformForm = this.fb.group(
        {
            platform: this.fb.group({
                name: this.fb.control('', [Validators.required]),
                lang: this.fb.group({
                    available: this.fb.control([], [Validators.minLength(1)]),
                    default: this.fb.control('', [Validators.required]),
                }),
                family: this.fb.group({
                    links: this.fb.control([]),
                    proofOfIdentityCount: this.fb.control(0),
                    expiration: this.fb.group({
                        ageRange: this.fb.array([
                            this.fb.control(null), // ageRange[0]
                            this.fb.control(null), // ageRange[1]
                        ]),
                        duration: this.fb.control(null),
                        noticeDelay: this.fb.control(null),
                    }),
                    maxFamilyMember: this.fb.control(null),
                    instructions: this.fb.control(null),
                }),
                modules: this.fb.control([]),
                consultation: this.fb.group({
                    accessType: this.fb.control(undefined),
                    provider: this.fb.control(undefined),
                }),
                cardPrint: this.fb.group({
                    canUserPrintCitizenCard: this.fb.control(true),
                }),
                emailChannelAutoSubscription: this.fb.control(false),
                frenchLanguageCharterBanner: this.fb.control(false),
                profileOptions: this.fb.group({
                    hiddenFields: this.fb.control([]),
                }),
                dashboardUrl: this.fb.group({
                    fr: this.fb.control(''),
                    en: this.fb.control(''),
                }),
            }),
        },
        { updateOn: 'change' },
    );

    constructor(private fb: FormBuilder) {}

    get modulesControl() {
        return this.platformForm.get('platform.modules') as FormGroup;
    }

    get consultationControl() {
        return this.platformForm.get('platform.consultation') as FormGroup;
    }

    ngOnInit(): void {
        this.modulesControl.valueChanges.subscribe((value) => {
            if (!value.includes(Module.CONSULTATION)) {
                this.consultationControl.reset();
            }
        });

        this.consultationControl.valueChanges.subscribe((value) => {
            if (value?.provider === ConsultationProvider.BCITI && !value?.accessType) {
                this.consultationControl.get('accessType').setValue(ConsultationConnectorAccessType.IFRAME);
            }
        });

        this.platformForm.get('platform.emailChannelAutoSubscription').valueChanges.subscribe((value) => {
            if (value === undefined || value === null) {
                this.platformForm.get('platform.emailChannelAutoSubscription').setValue(false);
            }
        });
    }

    submitPlatform() {
        const payload = this.platformForm.value;
        const expiration = payload.platform.family.expiration;
        //To handle when all attributes of expiration is null => expiration: null
        let isExpirationNull = true;

        if (expiration) {
            Object.keys(expiration).forEach((key) => {
                const value = expiration[key];

                if (Array.isArray(value) && value[0] !== null && value[1] !== null) {
                    isExpirationNull = false;
                } else if (typeof value !== 'object' && value !== null) {
                    isExpirationNull = false;
                }
            });
        }

        if (isExpirationNull) {
            payload.platform.family.expiration = null;
        }

        if (payload.platform.family.instructions === null) {
            payload.platform.family.instructions = {};
        }

        if (!this.availableLangs.includes('fr')) {
            payload.platform.dashboardUrl.fr = null;
        }

        if (!this.availableLangs.includes('en')) {
            payload.platform.dashboardUrl.en = null;
        }

        this.submitForm.emit(payload.platform as Partial<IPlatform>);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!!changes?.platform?.currentValue) {
            this.platformForm.patchValue({
                platform: { ...changes.platform?.currentValue },
            });
        }
        if (changes?.loading?.currentValue) {
            this.platformForm.disable();
        }
        if (!changes?.loading?.currentValue) {
            this.platformForm.reset(this.platformForm.value);
            this.platformForm.enable();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    get availableLangs() {
        return this.platformForm.get('platform.lang.available').value;
    }
}
